/* ==========================================================================
   Virtual Try-On (Fittingbox) — single controller
   --------------------------------------------------------------------------
   Replaces five separate copies of this logic that previously lived in
   layout/theme.liquid, snippets/product-grid-item.liquid,
   snippets/section-main-product.liquid, sections/product-slider.liquid and
   sections/custom-collection-showcase.liquid.

   What changed and why:
     - product-grid-item.liquid emitted this script ONCE PER PRODUCT CARD, and
       each copy looped over every card on the page doing one awaited fetch at
       a time. A 24-product collection fired ~576 serial requests. Availability
       is now deduped, cached and fetched in parallel, once per page.
     - The Fittingbox vendor script used to load on EVERY page and FitMix was
       called with no guard, so a blocked or slow CDN threw and silently killed
       every try-on button. It is now loaded on demand, on first click.
     - startTryOnCollection / startTryOnDynamic / checkAvailability were each
       defined multiple times and overwrote one another. Defined once here.
   ========================================================================== */
(function () {
  'use strict'

  var CONFIG = window.TRYON_CONFIG || {}
  var API_KEY = CONFIG.apiKey || ''
  var VENDOR_SRC = CONFIG.vendorSrc || 'https://vto-advanced-integration-api.fittingbox.com/index.js'
  var API_BASE = 'https://product-api.fittingbox.com/glasses-metadata/availability'
  var CONCURRENCY = 6
  var STORAGE_KEY = 'tryon:availability'

  if (!API_KEY) return

  /* ---------------------------------------------------------------- cache */

  var memoryCache = Object.create(null)

  function readSessionCache() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY)
      if (raw) Object.assign(memoryCache, JSON.parse(raw))
    } catch (e) {
      /* private mode, quota, disabled storage — cache stays in memory only */
    }
  }

  function writeSessionCache() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memoryCache))
    } catch (e) {
      /* non-fatal */
    }
  }

  readSessionCache()

  /* --------------------------------------------------------- availability */

  function fetchAvailability(upc) {
    var url = API_BASE + '?apiKey=' + encodeURIComponent(API_KEY) + '&uidList=' + encodeURIComponent(upc)

    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('Fittingbox responded ' + res.status)
        return res.json()
      })
      .then(function (data) {
        return !!(data && data[0] && data[0].available === true)
      })
      .catch(function () {
        // Treat an unreachable API as "not available" rather than throwing.
        // The button simply stays hidden; nothing else on the page breaks.
        return false
      })
  }

  // Resolves a map of { upc: boolean }. Deduped, cached, and run with a
  // bounded number of requests in flight instead of one-at-a-time awaits.
  function checkMany(upcs) {
    var unique = []
    var seen = Object.create(null)
    var result = Object.create(null)

    upcs.forEach(function (upc) {
      if (!upc || seen[upc]) return
      seen[upc] = true
      if (upc in memoryCache) {
        result[upc] = memoryCache[upc]
      } else {
        unique.push(upc)
      }
    })

    if (!unique.length) return Promise.resolve(result)

    var index = 0

    function worker() {
      if (index >= unique.length) return Promise.resolve()
      var upc = unique[index++]
      return fetchAvailability(upc).then(function (available) {
        memoryCache[upc] = available
        result[upc] = available
        return worker()
      })
    }

    var workers = []
    for (var i = 0; i < Math.min(CONCURRENCY, unique.length); i++) {
      workers.push(worker())
    }

    return Promise.all(workers).then(function () {
      writeSessionCache()
      return result
    })
  }

  /* ------------------------------------------------------- vendor loading */

  var vendorPromise = null

  function loadVendor() {
    if (vendorPromise) return vendorPromise

    vendorPromise = new Promise(function (resolve, reject) {
      if (typeof window.FitMix !== 'undefined') return resolve()

      var script = document.createElement('script')
      script.src = VENDOR_SRC
      script.async = true
      script.onload = function () {
        if (typeof window.FitMix === 'undefined') {
          reject(new Error('Fittingbox loaded but FitMix is undefined'))
        } else {
          resolve()
        }
      }
      script.onerror = function () {
        reject(new Error('Fittingbox script failed to load'))
      }
      document.head.appendChild(script)
    })

    return vendorPromise
  }

  function ensureWidget() {
    return loadVendor().then(function () {
      if (window.fitmixInstance) return window.fitmixInstance

      var container = document.getElementById('fitmix-container')
      if (!container) {
        container = document.createElement('div')
        container.id = 'fitmix-container'
        document.body.appendChild(container)
      }

      window.fitmixInstance = window.FitMix.createWidget('fitmix-container', {
        apiKey: API_KEY,
        popupIntegration: {
          centeredHorizontal: true,
          centeredVertical: true,
          width: '640px',
          height: '480px'
        }
      })

      return window.fitmixInstance
    })
  }

  /* --------------------------------------------------------------- toasts */

  var toastEl = null
  var toastTimer = null

  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div')
      toastEl.className = 'tryon-toast'
      toastEl.setAttribute('role', 'status')
      toastEl.setAttribute('aria-live', 'polite')
      document.body.appendChild(toastEl)
    }
    toastEl.textContent = message
    toastEl.hidden = false
    clearTimeout(toastTimer)
    toastTimer = setTimeout(function () {
      toastEl.hidden = true
    }, 4000)
  }

  /* ----------------------------------------------------------- launch VTO */

  function launch(frameId, button) {
    if (!frameId) {
      toast('Try-On is not available for this item.')
      return
    }

    if (button) {
      button.setAttribute('aria-busy', 'true')
      button.disabled = true
    }

    ensureWidget()
      .then(function (instance) {
        instance.setFrame(frameId)
        instance.startVto('live')
      })
      .catch(function () {
        toast('Try-On could not be started. Please try again.')
      })
      .then(function () {
        if (button) {
          button.removeAttribute('aria-busy')
          button.disabled = false
        }
      })
  }

  /* ------------------------------------------------------- grid / sliders */

  function gridButtons(root) {
    return Array.prototype.slice.call(
      (root || document).querySelectorAll('.tryon-wrapper button.tryon-btn[data-upc]')
    )
  }

  function initGrid(root) {
    var buttons = gridButtons(root).filter(function (btn) {
      return !btn.dataset.tryonBound
    })

    if (!buttons.length) return

    buttons.forEach(function (btn) {
      btn.dataset.tryonBound = 'true'
      if (!btn.getAttribute('type')) btn.setAttribute('type', 'button')
      if (!btn.getAttribute('aria-label')) btn.setAttribute('aria-label', 'Virtually try on this frame')
    })

    var upcs = buttons.map(function (btn) {
      return (btn.dataset.upc || '').trim()
    })

    checkMany(upcs).then(function (availability) {
      buttons.forEach(function (btn) {
        var upc = (btn.dataset.upc || '').trim()
        var wrapper = btn.closest('.tryon-wrapper')
        if (!wrapper) return
        wrapper.classList.toggle('is-available', !!availability[upc])
      })
    })
  }

  /* --------------------------------------------------------- product page */

  function currentVariantId() {
    var input =
      document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      document.querySelector('input[name="id"]') ||
      document.querySelector('select[name="id"]')
    return input ? input.value : null
  }

  function currentUpc() {
    var map = window.productUPCMap
    if (!map) return null
    var id = currentVariantId()
    if (!id) return null
    var upc = map[id]
    if (!upc || upc === 'null') return null
    upc = String(upc).trim()
    return upc || null
  }

  function refreshProductButton() {
    var wrapper = document.getElementById('tryon-wrapper')
    if (!wrapper) return

    var button = wrapper.querySelector('.tryon-btn')
    if (button) {
      if (!button.getAttribute('type')) button.setAttribute('type', 'button')
      if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', 'Virtually try on this frame')
    }

    var upc = currentUpc()
    if (!upc) {
      wrapper.classList.remove('is-available')
      return
    }

    checkMany([upc]).then(function (availability) {
      // The variant may have changed while the request was in flight.
      if (currentUpc() !== upc) return
      wrapper.classList.toggle('is-available', !!availability[upc])
    })
  }

  /* ---------------------------------------------- public (inline onclick) */

  window.startTryOn = function (frameId) {
    launch(frameId, null)
  }

  window.startTryOnCollection = function (btn) {
    if (!btn) return
    launch((btn.dataset.upc || '').trim(), btn)
  }

  window.startTryOnDynamic = function () {
    var wrapper = document.getElementById('tryon-wrapper')
    var btn = wrapper ? wrapper.querySelector('.tryon-btn') : null
    launch(currentUpc(), btn)
  }

  /* ----------------------------------------------------------------- boot */

  var refreshTimer = null

  function scheduleRefresh() {
    clearTimeout(refreshTimer)
    refreshTimer = setTimeout(function () {
      initGrid(document)
      refreshProductButton()
    }, 150)
  }

  function boot() {
    initGrid(document)
    refreshProductButton()

    // Variant changes on the product page. The theme's variant picker is a
    // custom element that dispatches namespaced events we can't wildcard on,
    // so we watch the form for both a change event and DOM replacement.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') scheduleRefresh()
    })

    var form = document.querySelector('form[action*="/cart/add"]')
    if (form && 'MutationObserver' in window) {
      new MutationObserver(scheduleRefresh).observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      })
    }

    // Ajax collection filtering / pagination re-renders the grid.
    document.addEventListener('collection:reloaded', scheduleRefresh)
    document.addEventListener('filter:selected', scheduleRefresh)
    document.addEventListener('collection-component:loaded', scheduleRefresh)
    document.addEventListener('shopify:section:load', scheduleRefresh)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot)
  } else {
    boot()
  }
})()
